import styles from './styles.module.scss';

import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Board } from '../../../../../../components/Board';
import { LogicManagerSidebar } from '../../../../../../components/LogicManagerSidebar';
import { ModuleOption } from '../../../../../../components/ModuleOption';
import { PopUpBox } from '../../../../../../components/PopUpBox';
import { ProfileBox } from '../../../../../../components/ProfileBox';
import { TextArea } from '../../../../../../components/TextArea';
import { verifyRoutePermissions } from '../../../../../../globalFunctions';
import { useFeedback } from '../../../../../../hooks/useFeedback';
import {
    LogicManagerModule,
    LOGICMANAGER_MODULES
} from '../../../../../../modules/config/LogicManagerModulesList';
import { LoadingContext } from '../../../../../../providers/LoadingProvider';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import { User, LoadData, Feedback } from '../../../../../../types';
import { saveWelcomeFeedback } from './requestHandlers';
import { Button } from '../../../../../../components/Button';
import { NavButton } from '../../../../../../components/NavButton';

export const LogicManagerModuleMaintainRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const { setIsLoadingContent } = useContext(LoadingContext);
    const navigate = useNavigate();

    // Control PopUpBox
    const [open, setOpen] = useState<boolean>(false);
    const welcomeFeedbackData: LoadData = useFeedback('intro');
    const [welcomeFeedback, setWelcomeFeedback] = useState<Feedback>();

    const renderModuleOptions = (module: LogicManagerModule) => {
        return (
            <ModuleOption
                key={module.id as React.Key}
                icon={module.icon}
                alt={module.name}
                moduleLink={`/manager/edit/module/${module.id}`}
            />
        );
    };

    const openEditWelcomeFeedback = () => {
        setOpen(true);
    };

    const onChangeWelcomeFeedback = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const feedback = Object.assign({}, welcomeFeedback);
        feedback.message = event.target.value;
        setWelcomeFeedback(feedback);
    };

    const onSaveWelcomeFeedback = async () => {
        if (welcomeFeedback) {
            try {
                setIsLoadingContent(true);
                const result = await saveWelcomeFeedback(userAuth.token, navigate, welcomeFeedback);

                if (result && typeof result === 'object' && Object.keys(result).includes('error')) {
                    throw result as Error;
                }

                setMessage({
                    type: 'success',
                    content: 'Saudação inicial alterada com sucesso'
                });
                setOpenMessage(true);
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const goBackwards = () => {
        if (!welcomeFeedbackData.isLoading) {
            navigate('/manager');
        }
    };

    useEffect(() => {
        if (!welcomeFeedbackData.isLoading) {
            const data = welcomeFeedbackData.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                setWelcomeFeedback(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [welcomeFeedbackData.isLoading]);

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.admin'],
            navigate
        );

        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />
            {userAuth.token && (
                <>
                    <ProfileBox user={userAuth} />

                    <Board wrapperClass={styles.board}>
                        <div className={styles.titleBar}>
                            <h1>Gerenciamento de módulos</h1>

                            <Button onClick={openEditWelcomeFeedback}>
                                Editar saudação inicial
                            </Button>
                        </div>

                        <div className={styles.moduleWrapper}>
                            {LOGICMANAGER_MODULES.map(renderModuleOptions)}
                        </div>
                    </Board>

                    <PopUpBox wrapperClass={styles.popUpBox} open={open} setOpen={setOpen}>
                        <h1 className={styles.popUpBoxTitle}>Saudação inicial</h1>

                        <TextArea
                            value={welcomeFeedback?.message}
                            onChange={onChangeWelcomeFeedback}
                            rows={4}
                        />

                        <Button
                            wrapperClass={styles.popUpBoxButton}
                            onClick={onSaveWelcomeFeedback}
                        >
                            Salvar
                        </Button>
                    </PopUpBox>
                </>
            )}
        </div>
    );
};
